// W5-2 — SAVE TO MY ROOM: ONE ROUTE, ONE LABEL, AND ONE ANSWER TO "WHICH READER OPENS THIS BOOK".
//
// Her ruling: every book's shelf/cover view offers Save to My Room, and her three books on Lala's
// Bookshelf were missing it entirely. Four surfaces now offer the same save — the category shelf, her
// cover shelf, the text reader and the page-image reader — and four copies of a member-facing string
// is how two of them end up saying different things.
//
// THE LAW THAT IS NOT COSMETIC, AND IT IS WHY THIS FILE EXISTS AT ALL (`readerKindFor`): a book on
// Lala's Bookshelf is held as designed PAGE IMAGES, not ingested text. My Room's saved shelf opened
// every saved book in the TEXT reader, so saving one of her three and tapping it in My Room landed on
// "being prepared" — a dead end wearing a book. Which reader a slug opens is a decision, it was made
// in two places, and only one of them was right.
//
// This module imports NOTHING (the entry requirement for `scripts/`-loadable law).

// Held by the gate against the server's own routing table: every fetch on this path in App.tsx ends
// in a `.catch(() => {})`, so a wrong URL is a silent 404 — no error, no save, no clue.
export const SAVED_BOOKS_URL = "/api/library/saved";

// The same row, added or removed.
export const saveMethod = (next: boolean) => (next ? "POST" : "DELETE");

// 14-B7d(2) — THE WORDS ARE A FALLBACK PARAMETER, NOT A REQUIRED ARGUMENT, AND THAT IS DECIDED BY
// WHO ELSE IS CALLING. This module imports NOTHING (r5's strip-loader entry requirement), so it
// cannot read the strings catalog; `sealFailureMessage(payload, fallback = SEAL_LOST_MESSAGE)` is
// the shape one module over. A REQUIRED argument finds forgotten call sites — but the callers here
// are two gates that call with nothing and read the English back (`w5-saved-books.mjs`,
// `r27-featured-tier.mjs`), so a required one would have broken instruments rather than found a bug.
export const SAVE_LABEL_SAVED = "✓ Saved to My Room";
export const SAVE_LABEL_ADD = "＋ Save to My Room";

// The reader's own button, in full. Both readers, one string.
export const saveButtonLabel = (saved: boolean, labels?: { saved?: string; add?: string }) =>
  (saved ? labels?.saved : labels?.add) || (saved ? SAVE_LABEL_SAVED : SAVE_LABEL_ADD);

// The shelf/cover chip has room for a mark, not a sentence, so its words live in the accessible
// name — which is also what a rendered gate can read back. `{title}` is filled here rather than at
// the call site, so a translation cannot put her book's name in the wrong half of the sentence.
export const SAVE_ARIA_SAVED = "{title} is saved to My Room";
export const SAVE_ARIA_ADD = "Save {title} to My Room";

export const saveShelfAriaLabel = (title: string, saved: boolean, templates?: { saved?: string; add?: string }) =>
  String((saved ? templates?.saved : templates?.add) || (saved ? SAVE_ARIA_SAVED : SAVE_ARIA_ADD))
    .replace("{title}", String(title ?? ""));

// A saved book opens the reader its pages actually live in. `featuredSlugs` is the server's own
// featured list (`/api/library/featured`), never a guess from the title.
export type ReaderKind = "page-image" | "text";
export const readerKindFor = (slug: string, featuredSlugs: readonly string[]): ReaderKind =>
  featuredSlugs.includes(slug) ? "page-image" : "text";

// ---------------------------------------------------------------------------------------------
// R59 — THE READS. Both of this file's routes were fetched by hand with a fabricated collection as
// the fallback (`r.ok ? r.json() : { books: [] }`), which is R55/R57/R58's defect a fourth time: the
// forged object IS an array under `books`, so the `Array.isArray` guard that looks like it is
// protecting her passes it straight through and the shelf paints EMPTY.
//
// **AND THE SHARPEST INSTANCE ON THIS ROUTE IS `useSavedShelf`'s `.catch(() => setSlugs([]))`.** That
// hook holds `string[] | null` and `null` IS THE "NOT LOADED YET" STATE — W5-2/R39's law, where
// `SaveToRoomButton`'s `saved === null` HOLDS STILL rather than claiming "not saved". So a failed
// read did not merely blank a list: it destroyed the exact distinction R39 built the null for, and
// **every save chip in the Library flipped from holding still to saying "not saved"** about books
// that are saved. A member re-saving one of them writes a row that is already there.
//
// So the answer is `T[] | null` (R58's shape, not the four-way one — a GET here refuses only by not
// arriving, and `handleSavedBooks`'s own `ensureUser` early return is now the same 200 + `{ok:false}`
// seven routes in this house answer with, read by SHAPE rather than by status code).
//
// `doFetch` is a PARAMETER (R46's locked-door shape) so a gate drives every answer without a
// browser, a session or a database.
// ---------------------------------------------------------------------------------------------

// Lala's Bookshelf — the family's own books. Held by the gate against the server's own routing table
// for the same reason `SAVED_BOOKS_URL` is: every fetch of it ended in a `.catch(() => {})`.
export const FEATURED_BOOKS_URL = "/api/library/featured";

async function listBooksAt<T>(url: string, doFetch: typeof fetch): Promise<T[] | null> {
  try {
    const res = await doFetch(url, { cache: "no-store" });
    if (!res.ok) { await res.body?.cancel?.().catch(() => {}); return null; }
    const data = (await res.json().catch(() => null)) as { books?: unknown } | null;
    return Array.isArray(data?.books) ? (data.books as T[]) : null;
  } catch {
    return null;
  }
}

// **A FAILED READ IS NOT AN EMPTY SHELF** (R18): null, so a surface keeps what it was showing and a
// chip that does not know yet goes on not knowing. A member who has saved nothing is a real
// `{ books: [] }` — the two are told apart by the SHAPE.
export const listSavedBooks = <T>(doFetch: typeof fetch = fetch) => listBooksAt<T>(SAVED_BOOKS_URL, doFetch);

// Her three books. A failed read here used to empty the shelf they stand on AND blank the map My
// Room resolves a saved slug through — which is `readerKindFor`'s own input, so a saved featured
// book quietly became a text-reader dead end again (the exact defect W5-2 exists to have fixed).
export const listFeaturedBooks = <T>(doFetch: typeof fetch = fetch) => listBooksAt<T>(FEATURED_BOOKS_URL, doFetch);
